import documents from "./supplied-documents.json";
import { confirmationSequence, updateOrderedConfirmation } from "./confirmation-sequence";

export type DocumentRole = "creator" | "merchant" | "agent";
export const REGISTRATION_SENSITIVE_VERSION = "registration-sensitive-2026-10-29-v1";
export const suppliedConsentKeys = ["identity", "face", "materials", "financial"] as const;
export type SuppliedConsentChoices = Record<typeof suppliedConsentKeys[number], boolean>;
export const emptySuppliedChoices = (): SuppliedConsentChoices => ({ identity: false, face: false, materials: false, financial: false });
export type SuppliedDocumentSignatures = { digest: string; signed: Record<string, boolean>; choices: SuppliedConsentChoices; complete: boolean };
export function validSuppliedChoices(value: unknown): value is SuppliedConsentChoices {
  return Boolean(value && typeof value === "object" && !Array.isArray(value)
    && Object.keys(value).length === suppliedConsentKeys.length
    && suppliedConsentKeys.every(key => typeof (value as Record<string, unknown>)[key] === "boolean"));
}
export function acceptedSuppliedChoices(value: unknown): value is SuppliedConsentChoices {
  return validSuppliedChoices(value) && confirmationSequence(suppliedConsentKeys, key => value[key]).complete;
}
export function suppliedChoiceUnlocked(choices: SuppliedConsentChoices, index: number) {
  return confirmationSequence(suppliedConsentKeys, key => choices[key]).isUnlocked(index);
}
export function updateSuppliedChoice(choices: SuppliedConsentChoices, index: number, value: boolean): SuppliedConsentChoices {
  if (!suppliedChoiceUnlocked(choices, index)) return choices;
  return updateOrderedConfirmation(suppliedConsentKeys, choices, suppliedConsentKeys[index], value, false, (_key, choice) => choice);
}
export function suppliedDocumentSequence(documents: readonly { id: string }[], signed: Record<string, boolean>) {
  const sequence = confirmationSequence(documents, document => signed[document.id] === true);
  return { ...sequence, isUnlocked: (index: number) => documents[index]?.id === "sensitive" || sequence.isUnlocked(index) };
}
export function suppliedDocumentsForRole(role: DocumentRole) {
  return documents.filter(document => document.roles.includes(role));
}
